import type { CardGame } from "./games.ts";

/**
 * The sections of a game's rules, in the order the page and the PDF's first page both print them
 * (ADR 0135 §2), with the heading the PDF prints. The page's headings come from the
 * `cardGames.*` catalogue strings; `cardGames.test.ts` checks the two agree.
 *
 * Plain data and no renderer, so the page can import it without the PDF code.
 */
export type RuleSectionKey = "goal" | "youNeed" | "setUp" | "howToPlay" | "example" | "winning" | "easier" | "harder" | "practises";

export interface RuleSection {
    key: RuleSectionKey;
    heading: string;
    /** A list is bulleted, or numbered when `numbered`; a paragraph is one block of text. */
    kind: "paragraph" | "bullets" | "numbered";
    /** The section's text: one entry for a paragraph. */
    items: string[];
}

export const RULE_HEADINGS: Record<RuleSectionKey, string> = {
    goal: "Goal",
    youNeed: "You need",
    setUp: "Set up",
    howToPlay: "How to play",
    example: "Example",
    winning: "Who wins",
    easier: "Make it easier",
    harder: "Make it harder",
    practises: "What it practises",
};

export function ruleSections(game: CardGame, headings: Record<RuleSectionKey, string> = RULE_HEADINGS): RuleSection[] {
    const section = (key: RuleSectionKey, kind: RuleSection["kind"], items: string[]): RuleSection => ({
        key,
        heading: headings[key],
        kind,
        items,
    });
    return [
        section("goal", "paragraph", [game.goal]),
        section("youNeed", "bullets", game.youNeed),
        section("setUp", "bullets", game.setUp),
        section("howToPlay", "numbered", game.howToPlay),
        section("example", "paragraph", [game.example]),
        section("winning", "paragraph", [game.winning]),
        section("easier", "paragraph", [game.easier]),
        section("harder", "paragraph", [game.harder]),
        section("practises", "bullets", game.practises),
    ];
}

/** "Grades K–1 · 2–4 players · 10 minutes" */
export function quickFacts(game: CardGame): string {
    return [game.grades, game.players, game.time].join(" · ");
}

/** `make-ten-catch.pdf` from "Make Ten Catch". */
export function cardGameFileName(game: CardGame): string {
    return game.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") + ".pdf";
}

/** How many printed pages: the rules, the card sheets (8 to a sheet), the record sheets (2 to a page). */
export function cardGamePageCount(game: CardGame, cardsPerSheet = 8): number {
    const records = game.recordSheet ? Math.ceil(game.recordSheet.copies / 2) : 0;
    return 1 + Math.ceil(game.deck.length / cardsPerSheet) + records;
}
